// Typed event catalog -- the single source of truth for what may be tracked.
//
// Every event name is snake_case object_action form. Each event declares the
// exact set of caller-supplied property keys it allows; the privacy guard
// (privacy.ts) drops any key not on this whitelist before an event is dispatched.
// Enrichment fields (app_version, session_id, source, surface, doc_kind,
// unit_system, event, timestamp) are added by track() AFTER this filtering and
// are NOT part of these per-event whitelists.
//
// Adding an event: add its name + allowed keys here and TypeScript enforces the
// rest at every call site. Keep the catalog tight -- prefer bucketed dimensions
// (see EVENT_SCHEMA `*_bucket` keys) over raw values.

/** Every allowed event name. The union is derived from EVENT_SCHEMA below. */
export const EVENT_SCHEMA = {
    // -- Session ------------------------------------------------------------
    // visual_viewport_scale / page_scroll_y: see analytics/pageViewport.ts --
    // != 1 / != 0 means the browser zoomed or scrolled the PAGE under the app.
    // pdf_*/page_*/has_*/is_*: what kind of PDF it is (analytics/pdfFormat.ts) --
    // closed sets only; the producer is a FAMILY, never the raw Producer/Creator.
    document_opened: [
        'doc_kind', 'page_bucket', 'markup_count_bucket', 'visual_viewport_scale', 'page_scroll_y',
        'pdf_version', 'pdf_standard', 'pdf_producer', 'page_size', 'page_orientation',
        'has_text_layer', 'is_linearized', 'has_forms', 'has_xfa', 'has_signatures', 'is_encrypted',
        'pdf_source',
    ],
    document_uploaded: ['page_bucket', 'size_bucket_mb', 'seeded_markups_bucket'],
    project_created: ['unit_system'],
    auth_login: [],
    auth_signup: ['method'],
    cache_cleanup_failed: [],

    // -- Marketing site -----------------------------------------------------
    // A call to action in the site top bar (components/marketing/SiteHeader.tsx) was clicked. Fire-and-forget:
    // the link navigates as usual. cta: 'open_pdf' | 'sign_in' | 'install' (the Menu's Install app, shown only
    // when the browser offers an install); placement: 'bar' (the row) | 'menu' (the phone / iPad Menu sheet).
    // The page is the event's own URL, not a prop.
    site_cta_clicked: ['cta', 'placement'],
    // The app was installed (the browser's `appinstalled`: from our Menu's Install app or the browser's own install
    // UI). src/pwa/installPrompt.ts; docs/offline.md.
    pwa_installed: [],

    // -- Markup -------------------------------------------------------------
    tool_selected: ['tool', 'sticky'],
    // scale_source (measurable types only): where the markup's real-world scale
    // came from -- core/scale/resolveScale.ts ResolvedScale.source, or 'none'.
    markup_created: ['markup_type', 'scale_source'],
    // A new image or photo pin (image markup spec 6, M7): source_kind = the cover photo's MediaItem source.kind
    // (paste | clipboard | drop | file | camera | pdf-import); markup_type = image | photo-pin; has_source_url: the
    // copy carried a link (the URL itself never leaves); size_bucket_mb: the stored photos' bytes; count_bucket: how
    // many photos; room (photo-pin only): auto | typed | none | no-rooms | no-package. No url / caption / name / asset.
    image_added: ['source_kind', 'markup_type', 'has_source_url', 'size_bucket_mb', 'count_bucket', 'room'],
    markup_edited: ['markup_type', 'edit_kind'],
    markup_deleted: ['markup_type'],
    // A markup command threw or its store write was refused (see
    // analytics/markupFailure.ts). command: the MarkupCommand type
    // ('markup/delete', ...); failure_kind: closed slug (no-edit-permission |
    // denied | refused | network | aborted | unknown); error_code: the error's own
    // short code slug ('permission-denied', 'illegal-transition', ...) or 'none'.
    // Never `reason`/`message` -- those keys are forbidden (free text).
    markup_command_failed: ['command', 'failure_kind', 'error_code'],
    // The same failure narrowed to deletes, so "delete did nothing" is one query.
    markup_delete_failed: ['failure_kind', 'error_code'],
    markup_grouped: ['child_count_bucket'],
    markup_ungrouped: ['child_count_bucket'],
    // Pencil hold-to-snap replaced a freehand stroke. from: 'freehand';
    // to: the markup type written (cloud|ellipse|rectangle|polygon|arrow|
    // callout); mode: the Snap-shapes setting that fired it ('hold'|'always');
    // type: what the recogniser called it (circle|ellipse|cloud|rect|polygon,
    // else = to); alternatives: the convert pill's other readings, nearest
    // first, comma-joined labels ('' for open strokes). Sent again when the
    // pill converts the shape, with converted_to = the label tapped (and to =
    // its markup type): events WITH converted_to are conversions, not snaps.
    markup_shape_snapped: ['from', 'to', 'mode', 'type', 'alternatives', 'converted_to'],
    // Every snappable (pen/finger, setting not off) freehand stroke end: did
    // hold-to-snap work, and if not which gate failed. outcome: snapped |
    // no-hold (the hold never fired or was cancelled) | low-confidence (held;
    // nearest shape missed a threshold) | null (held; nothing shape-like).
    // candidate: winner or nearest miss; confidence: limit/(limit+residual),
    // 0.5 = at threshold; fail_check: the recogniser's failing check.
    // hold_ms / max_jitter_px (CSS): the stillness at lift. hold_threshold_ms /
    // hold_tolerance_px: the live-tunable constants in force. paired: a
    // two-stroke callout. loops: turns around the stroke's centroid (1 = one
    // loop); turning_deg: summed heading change of the cleaned stroke. No
    // geometry leaves the device.
    markup_shape_snap_attempted: ['outcome', 'candidate', 'confidence', 'fail_check', 'hold_ms', 'max_jitter_px', 'point_count', 'pointer_type', 'mode', 'paired', 'zoom', 'hold_threshold_ms', 'hold_tolerance_px', 'loops', 'turning_deg'],
    // Ink feel baseline (core/ink/inkMetrics.ts), 1 in 5 strokes of 3+ moves.
    // latency = next animation frame minus the pointermove timeStamp (ms);
    // coalesced/predicted = samples per dispatched move; safari_version is the
    // UA's Version/x.y (absent off Safari); desync 1 = the ink canvas really
    // runs desynchronized. replayed_samples: coalesced samples dropped as
    // repeats of an earlier move's batch (iPad Safari); predicted_drawn:
    // predicted points painted as the tail; hover_reports: Pencil hover
    // reports since the previous stroke. Counts and times only -- no coordinates.
    ink_stroke_metrics: ['pointer_type', 'tool', 'safari_version', 'move_events', 'samples', 'duration_ms', 'sample_rate_hz', 'latency_p50_ms', 'latency_p95_ms', 'latency_max_ms', 'coalesced_per_move_avg', 'coalesced_per_move_max', 'predicted_per_move_avg', 'predicted_available', 'pressure_min', 'pressure_max', 'desync', 'replayed_samples', 'predicted_drawn', 'hover_reports'],
    pdf_layer_visibility_toggled: ['enabled'],
    // Contextual snap bar: which control was toggled and its new boolean state.
    // control: 'markup' | 'content' | 'axis' | 'vertices' | 'alignment'.
    snap_toggled: ['control', 'enabled'],
    // Auto input mode changed (the 3-way stylus control: 'auto' | 'on' | 'off'
    // -- see core/input/inputRouting.ts; 'on'/'off' are the old stylus-only
    // toggle's two states, 'auto' is the new default). Replaces the old
    // boolean stylus_only_toggled event. input_mode is the latched
    // mouse|touch|pen; the viewport pair is the page-zoom fingerprint.
    input_mode_changed: ['mode', 'input_mode', 'visual_viewport_scale', 'page_scroll_y'],
    edit_undo: [],
    edit_redo: [],
    // A review decision, wayfinding step or context-menu move, and WHERE the
    // reviewer made it (docs/review-ui.md: decisions live on the rail card).
    // action: 'accept' | 'reply' | 'leave_out' (a Reply from the item checklist) | 'withdraw' (ex-'release', spec--review-flow RF7) | 'try_again' (a Couldn't apply card's preset Reply) | 'restore' | 'reapply' | 'fix_reading' |
    // 'delete' | 'select_set' | 'next' | 'prev' | 'start' | 'go' (U3 head stepper) |
    // 'advance' (U3 auto-advance opened the next card) | 'show' (a receipt
    // toast's Show); from: 'card' (the expanded rail card) | 'context'
    // (long-press menu on a row or pill) | 'queue' (the rail head's your-turn
    // stepper) | 'auto' (auto-advance) | 'receipt' (the receipt toast) | 'list'
    // (the markup tree view). No markup id, summary or reason.
    // trace_id: the proposal primary's delta.trace_id when known (see above).
    proposal_card_action: ['action', 'from', 'trace_id'],
    // SEND (`request.dispatch`, the drafter's only intake) tapped on the
    // selection bar (FloatingActionBar): from and source are 'selection_menu'
    // (U4: the rail header's and the card's Send are gone; the names stay for
    // dashboard continuity). No markup ids, count or scope beyond that.
    dispatch_tapped: ['from', 'source'],
    // This tab switched to another review session without a reload. from:
    // 'link' (a join link handed over from another tab, or pasted) | 'code'
    // (typed in the rail's Join session sheet) | 'recent' (the Recent sessions
    // list). No session id or code.
    session_switched: ['from'],
    // The review rail's bottom sheet changed detent. detent: 'peek' | 'half' |
    // 'full'; via: 'tap' | 'swipe' | 'button'.
    review_rail_detent: ['detent', 'via'],
    // A sheet badge in the rail's sheet strip was tapped. kind: the badge's
    // leading state ('proposal' | 'attention' | 'plain'); filtered: the list is
    // now filtered to that sheet (false = the filter was cleared).
    sheet_badge_tap: ['kind', 'filtered'],
    // A request badge on the drawing was tapped (UI-FINAL U2). kind: 'badge' (selects that request) | 'stack'
    // (zooms to fit the requests merged under it); card: the (first) request's card state. No number or id.
    request_badge_tapped: ['kind', 'card'],
    // A view-link head (section / elevation / callout / detail, bim-pdf 1.13) was tapped (wiki spec--view-links).
    // kind: the ref kind; outcome: 'landed' | 'choose' (a parent view split into dependents) | 'miss' (not in this
    // set) | 'moved' (landed, but the printed sheet differs); input: 'touch' | 'pen' | 'mouse'; where: 'document'
    // (this document's scales) | 'set' (another Set document, hint-then-verify). No view id or sheet.
    view_link_followed: ['kind', 'outcome', 'input', 'where'],
    // The view-link chip's Back restored the previous camera. depth: stack depth before the pop (bucketed).
    view_link_back: ['depth'],
    // The sheet strip opened a Set page of ANOTHER document (document control:
    // the viewer switched documents).
    set_page_opened: [],
    // The Set was edited from the sheet strip (set.update). action: reorder |
    // exclude | include; outcome: ok | stale | failed.
    set_edited: ['action', 'outcome'],
    // "Upload new version" (document.upload): outcome ok | noop | a refusal
    // code (stale-revision, no-sheet-numbers, bad-pdf, ...); match: how pages
    // matched (page_id | position | sheet | new | replay | auto).
    document_version_uploaded: ['outcome', 'match'],
    // A BIM element tapped on the canvas selected the markups linked to it
    // (selection is the join key). markup_count: bucketed.
    element_select_from_canvas: ['markup_count'],

    // -- Measure / scale ----------------------------------------------------
    scale_configured: ['method'],
    measurement_completed: ['measure_kind', 'scale_source'],
    // A dimension was drawn with NO resolved scale (its label reads "240 pt -- set scale"; D3 of
    // spec--pdfmarkuptool-input-primitives). The one-time "Set scale" toast rides on the first one. No props:
    // no length, no coordinates.
    dimension_unscaled_shown: [],

    // -- BIM ----------------------------------------------------------------
    // doc_kind is explicit here: this fires inside the package load, BEFORE the
    // viewer's ambient doc_kind can know the answer.
    bimpdf_opened: ['has_elements', 'element_count_bucket', 'doc_kind'],
    bim_overlay_toggled: ['enabled'],
    bim_overlay_facet_visibility_toggled: ['enabled'],
    bim_element_inspected: [],
    // BIM mode's Move released into a move markup. constraint: free | axis | host; snap: the osnap kind the
    // release landed on, or 'none'; input: touch | pen | mouse. No element id, no distance.
    bim_element_moved: ['constraint', 'snap', 'input'],
    bim_cloud_linked: ['element_count_bucket'],
    bim_callout_attached: ['attached_to'],
    model3d_viewer_toggled: ['enabled'],

    // -- Export -------------------------------------------------------------
    export_completed: ['format', 'mode', 'markup_count_bucket'],
    export_failed: ['format', 'mode', 'error_kind', 'markup_count_bucket'],

    // -- Import -------------------------------------------------------------
    // annotations.json (bim-pdf sidecar) import via the markups toolbar. Both
    // counts are bucketized by the privacy guard (never raw): imported = drafts
    // inserted, skipped = drafts whose id already existed (idempotent re-import).
    annotations_imported: ['imported_count_bucket', 'skipped_count_bucket'],

    // -- Boot / load --------------------------------------------------------
    // Fired on every local-viewer page load once the restore outcome is known.
    // restore_outcome: 'ok' | 'failed'
    app_booted: ['restore_outcome'],

    // Storage health snapshot taken after the PDF-cache lookup on local-viewer load.
    // persisted: whether the browser has granted persistent storage for this origin
    // usage_bucket_mb / quota_bucket_mb: navigator.storage.estimate() values, MB-bucketed
    // cache_hit: whether the PDF blob was present in the IndexedDB cache
    storage_health_measured: ['persisted', 'usage_bucket_mb', 'quota_bucket_mb', 'cache_hit'],

    // -- Storage ------------------------------------------------------------
    // Outcome of requesting persistent storage on the local-viewer path.
    // outcome: 'granted' | 'denied' | 'unsupported'.
    storage_persistence_requested: ['outcome'],

    // -- Failures -----------------------------------------------------------
    // General-purpose failure event for non-export operations (e.g. React error
    // boundaries). error_kind is a closed slug from classifyError(), never raw text. error_site is the innermost
    // app component name of a render crash (identifier-shaped, from crashSite()), or 'unknown'.
    // error_name / crash_frames / component_frames: core/diagnostics/crashFrames.ts --
    // code locations only (bundle chunk:line:col), never the error message.
    operation_failed: ['operation', 'error_kind', 'error_site', 'error_name', 'crash_frames', 'component_frames'],

    // Fired when the local-viewer restore path fails to mount the viewer.
    // restore_reason: 'no_workspace' | 'workspace_expired' | 'pdf_missing' | 'error'
    // has_markups: whether the workspace had saved markups at the time of failure
    workspace_restore_failed: ['restore_reason', 'has_markups'],

    // Global unhandled promise rejection caught by the window listener.
    // error_kind: closed slug from classifyError() -- never raw error text.
    promise_rejection_unhandled: ['error_kind', 'error_name', 'crash_frames'],
} as const;

/** Allowed event names (compile-time enforced at call sites). */
export type EventName = keyof typeof EVENT_SCHEMA;

/**
 * Caller-supplied properties. Intentionally loose (string keys -> primitive
 * values) so call sites stay ergonomic; the privacy guard enforces the actual
 * per-event whitelist + forbidden-key rules at runtime. Values may be raw
 * numbers for `*_bucket` keys -- the guard bucketizes them.
 */
export type EventProps = Record<string, string | number | boolean | undefined>;

/** A fully-enriched event as it reaches the sinks. */
export interface EnrichedEvent {
    event: EventName;
    /** ISO timestamp of when the event was tracked. */
    timestamp: string;
    /** 'human' | 'agent' -- derived from the actor context, never a literal. */
    source: string;
    /** 'gui' | 'agent-api' -- how the event was driven. */
    surface: string;
    /** App version from /version.json (or build var / 'dev'). */
    app_version: string;
    /** Random id, stable for the page load. */
    session_id: string;
    /** Whitelisted, bucketized, privacy-scrubbed caller props + ambient dims. */
    props: Record<string, string | number | boolean>;
}
